import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatMAD } from "@/lib/api";
import { buildOrderWhatsAppUrl } from "@/lib/whatsapp";
import { useI18n } from "@/context/I18nContext";
import { WhatsappIcon } from "@/components/WhatsAppFab";
import { CheckCircle2, Truck, Phone, ArrowRight, Package } from "lucide-react";

export default function OrderConfirmation() {
    const { orderNumber } = useParams();
    const { t } = useI18n();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/orders/${orderNumber}`)
            .then((r) => setOrder(r.data))
            .catch(() => setOrder(null))
            .finally(() => setLoading(false));
    }, [orderNumber]);

    if (loading) {
        return (
            <div className="mx-auto max-w-[900px] px-5 sm:px-8 py-32 text-center">
                <div className="h-8 bg-white/5 animate-pulse w-64 mx-auto" />
            </div>
        );
    }

    if (!order) {
        return (
            <div className="mx-auto max-w-[900px] px-5 sm:px-8 py-32 text-center">
                <p className="font-display text-3xl uppercase font-black">{t("order.not_found")}</p>
            </div>
        );
    }

    return (
        <div data-testid="order-confirmation-page" className="mx-auto max-w-[900px] px-5 sm:px-8 py-16 sm:py-24">
            <div className="border border-white/10 p-8 sm:p-12">
                <div className="flex items-center justify-center w-16 h-16 border border-white mb-8 mx-auto">
                    <CheckCircle2 size={32} strokeWidth={1.5} />
                </div>

                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3">
                        {t("order.thank_you")}
                    </p>
                    <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter">
                        {t("order.title")}
                    </h1>
                    <p className="mt-5 text-sm text-white/60 max-w-md mx-auto">
                        {t("order.contact_soon_1")}{" "}
                        <span className="text-white">{order.customer.phone}</span>{" "}
                        {t("order.contact_soon_2")}
                    </p>
                </div>

                <div className="mt-10 border-t border-white/10 pt-8 grid sm:grid-cols-2 gap-6">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                            {t("order.number")}
                        </p>
                        <p className="font-display text-lg font-bold" data-testid="order-number">
                            {order.order_number}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                            {t("order.delivery_to")}
                        </p>
                        <p className="text-sm">
                            {order.customer.name}
                            <br />
                            {order.customer.address}, {order.customer.city}
                        </p>
                    </div>
                </div>

                <div className="mt-8 border-t border-white/10 pt-6 space-y-3">
                    {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                            <span className="text-white/70">
                                {item.quantity}× {item.name}
                                {item.size ? ` (${item.size})` : ""}
                            </span>
                            <span>{formatMAD(item.price * item.quantity)}</span>
                        </div>
                    ))}
                    {order.discount > 0 && (
                        <div
                            className="flex justify-between text-sm text-white pt-3 border-t border-white/10"
                            data-testid="order-discount-row"
                        >
                            <span>
                                {t("checkout.discount")}
                                {order.promo_code ? ` · ${order.promo_code}` : ""}
                            </span>
                            <span>−{formatMAD(order.discount)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-sm text-white/70 pt-3 border-t border-white/10">
                        <span>{t("cart.shipping")}</span>
                        <span>{formatMAD(order.shipping_fee)}</span>
                    </div>
                    <div className="flex justify-between font-display uppercase font-bold text-lg pt-3 border-t border-white/10">
                        <span>{t("order.total_pay")}</span>
                        <span>{formatMAD(order.total)}</span>
                    </div>
                </div>

                <div className="mt-8 border border-white/20 p-5 bg-white/[0.03] flex items-start gap-4">
                    <Truck size={20} strokeWidth={1.5} className="flex-shrink-0 mt-1" />
                    <div>
                        <p className="font-display text-sm uppercase font-bold tracking-tight">
                            {t("order.cod_title")}
                        </p>
                        <p className="text-sm text-white/60 mt-1">
                            {t("order.cod_desc", { amount: formatMAD(order.total) })}
                        </p>
                    </div>
                </div>

                <a
                    href={buildOrderWhatsAppUrl(order, formatMAD)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 flex items-center gap-4 border border-[#25D366]/60 bg-[#25D366]/10 hover:bg-[#25D366]/20 p-5 transition-colors group"
                    data-testid="order-whatsapp-btn"
                >
                    <span className="flex items-center justify-center w-11 h-11 bg-[#25D366] text-white flex-shrink-0">
                        <WhatsappIcon size={22} />
                    </span>
                    <span className="flex-1 text-start">
                        <p className="font-display text-sm uppercase font-bold tracking-tight">
                            {t("whatsapp.send_order")}
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                            {t("whatsapp.send_order_desc")}
                        </p>
                    </span>
                    <ArrowRight size={16} className="text-white/60 group-hover:text-white transition-colors rtl:rotate-180" />
                </a>

                {order.tracking_link && (
                    <a
                        href={order.tracking_link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 flex items-center gap-4 border border-white/20 hover:border-white p-5 transition-colors group"
                        data-testid="order-tracking-link"
                    >
                        <span className="flex items-center justify-center w-11 h-11 border border-white text-white flex-shrink-0">
                            <Package size={20} strokeWidth={1.5} />
                        </span>
                        <span className="flex-1 text-start">
                            <p className="font-display text-sm uppercase font-bold tracking-tight">
                                {t("order.tracking_title")}
                            </p>
                            <p className="text-xs text-white/60 mt-1 truncate">
                                {t("order.tracking_desc")}
                            </p>
                        </span>
                        <ArrowRight size={16} className="text-white/60 group-hover:text-white transition-colors rtl:rotate-180" />
                    </a>
                )}

                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <Link
                        to="/collection"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-12 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                        data-testid="order-back-shop-btn"
                    >
                        {t("order.continue_shop")} <ArrowRight size={14} className="rtl:rotate-180" />
                    </Link>
                    <a
                        href="tel:+212000000000"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-12 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-black transition-colors"
                    >
                        <Phone size={14} /> {t("order.contact_us")}
                    </a>
                </div>
            </div>
        </div>
    );
}
